import { TPlan } from "@/components/Checkout/types.checkout";
import { useUserCreditQuery } from "@/components/Referral/hooks/useUserCreditQuery";
import { useCheckoutDataQuery } from "@/components/Root/hooks/useCheckoutDataQuery";
import useUserPlanData from "@/components/Root/hooks/useUserPlanData";
import { Button } from "@/components/ui/button";
import { LoaderSpinner } from "@/components/ui/Loader/Spinner";
import RotatingBorder from "@/components/ui/rotating-border";
import useAppQueryParam from "@/hooks/useAppQueryParam";
import {
  LOCALISED_PRICING_SOURCE,
  useLocalisedPricing,
} from "@/hooks/useLocalisedPricing";
import { useSearchParams } from "@/lib/router";
import eventTrackingHandler from "@/tracking/eventTrackingHandler";
import { TBillingInterval } from "@/types/app";
import formatTemplateString from "@/utils/formatTemplateString";
import { parsePromoCode } from "@/utils/parseQuery";
import parse from "html-react-parser";
import { useEffect, useRef, useState } from "react";
import { SparkleIcon } from "@pkg/icons";
import { TDashboardMainConfig } from "../config.dashboard";
import { SWITCH_PLAN_MODAL_PARAM } from "../constants.dashboard";
import { BASE_DASHB_EVENT_PROPS, DASHBOARD_EVENTS } from "../events.dashboard";
import { SwitchIntervalCard } from "@/components/shared/interval-switch/switch-interval-card";
import { SwitchIntervalDialog } from "@/components/shared/interval-switch/switch-interval-dialog";
import { useEstimateIntervalChangeQuery } from "@/components/shared/interval-switch/use-estimate-interval-change-query";
import { DashboardService } from "../service.dashboard";
import type { buildSwitchIntervalCopy } from "@/components/Checkout/utils/switch-interval-copy";

function calcPricingSummary(
  ownedPlanList: TPlan[],
  billingPeriod: TBillingInterval,
) {
  const monthlyTotal = ownedPlanList.reduce((total, plan) => {
    return total + (plan.pricing.discountedMonthly ?? plan.pricing.monthly);
  }, 0);

  const yearlyTotal = ownedPlanList.reduce((total, plan) => {
    return total + (plan.pricing.discountedYearly ?? plan.pricing.yearly);
  }, 0);

  const currentCost = billingPeriod === "yearly" ? yearlyTotal : monthlyTotal;
  const discount = monthlyTotal * 12 - yearlyTotal;
  const savePercentage =
    monthlyTotal > 0 ? Math.round((discount / (monthlyTotal * 12)) * 100) : 0;

  return { currentCost, discount, savePercentage, total: yearlyTotal };
}

interface YearlyPlanSaleBannerProps {
  conpyConfig: TDashboardMainConfig["yearly_plan_sale_banner"];
  switchIntervalCopy: ReturnType<typeof buildSwitchIntervalCopy>;
  currentPeriodEnd?: number;
  ownedPlanList: TPlan[];
  billingPeriod: TBillingInterval;
  loading: boolean;
  isV1?: boolean;
}

const YearlyPlanSaleBanner = ({
  conpyConfig,
  switchIntervalCopy,
  currentPeriodEnd,
  ownedPlanList,
  billingPeriod,
  loading,
  isV1 = false,
}: YearlyPlanSaleBannerProps) => {
  const { getQueryParam, addQueryParam, removeQueryParam } = useAppQueryParam();
  const [searchParams] = useSearchParams();

  // Prefill the promo code from the URL: ?promo= | ?promo_code= | ?coupon= | ?discount=
  const queryPromoCode = parsePromoCode(searchParams) ?? "";
  const [promoCode, setPromoCode] = useState(queryPromoCode);

  const switchPlanModal = getQueryParam(SWITCH_PLAN_MODAL_PARAM) === "true";
  const switchIntervalContent = DashboardService.Content.switchIntervalCard;

  // The proration footnote only applies during the sale (?sale=true)
  const isSaleActive = getQueryParam("sale") === "true";

  const openSwitchPlanModal = () =>
    addQueryParam(SWITCH_PLAN_MODAL_PARAM, "true");
  const closeSwitchPlanModal = () => removeQueryParam(SWITCH_PLAN_MODAL_PARAM);

  useUserCreditQuery();

  // Determine target interval (switch from monthly to yearly or vice versa)
  const TARGET_INTERVAL: TBillingInterval = "yearly";

  const planCurrency = ownedPlanList[0]?.pricing.currency ?? "USD";

  const { data: userPlanData } = useUserPlanData();
  const { data: userCheckoutData } = useCheckoutDataQuery();

  const { isLocalisedPricing } = useLocalisedPricing({
    source: LOCALISED_PRICING_SOURCE.METADATA,
    planCurrency: userCheckoutData?.planCards?.[0]?.pricing.currency,
    userCountryCode: userPlanData?.country_code,
  });

  const pricingSummary = calcPricingSummary(ownedPlanList, billingPeriod);
  const { savePercentage, currentCost, discount } = pricingSummary;

  // Fetch estimate when modal opens
  const {
    data: estimateData,
    isFetching: isEstimating,
    isError: isEstimateError,
    refetch: refetchEstimate,
  } = useEstimateIntervalChangeQuery({
    targetInterval: TARGET_INTERVAL,
    promoCode,
    enabled: switchPlanModal, // Only run when modal is open
    isV1,
    isLocalisedPricing,
    currency: planCurrency,
  });

  const handleApplyPromo = async (code: string) => {
    const trimmedCode = code.trim();
    if (!trimmedCode) return;

    setPromoCode(trimmedCode);
  };

  const handleRemovePromo = async () => {
    setPromoCode("");
  };

  const entryPointRef = useRef<"banner_click" | "deep_link">("deep_link");

  function handleSwitchToYearlyClick() {
    entryPointRef.current = "banner_click";
    eventTrackingHandler(DASHBOARD_EVENTS.SWITCH_TO_YEARLY, {
      types: ["posthog", "customerio"],
      properties: {
        ...BASE_DASHB_EVENT_PROPS,
        current_billing_period: billingPeriod,
        current_cost: currentCost,
        save_percentage: savePercentage,
        discount_amount: discount,
        plan_count: ownedPlanList.length,
      },
    });
    openSwitchPlanModal();
  }

  // Now that the dialog's open state lives in the URL it can open without a click,
  // so the funnel's first step has to be measured on open rather than in the click
  // handler — otherwise campaign traffic arriving on ?upgrade-interval=true is
  // invisible until it converts.
  useEffect(() => {
    if (!switchPlanModal) {
      entryPointRef.current = "deep_link";
      return;
    }

    eventTrackingHandler(DASHBOARD_EVENTS.INTERVAL_SWITCH_MODAL_VIEWED, {
      types: ["posthog", "customerio"],
      properties: {
        ...BASE_DASHB_EVENT_PROPS,
        entry_point: entryPointRef.current,
        // the code the campaign link carried, before the user edits it
        promo_code: queryPromoCode || undefined,
        current_billing_period: billingPeriod,
        current_cost: currentCost,
        save_percentage: savePercentage,
        discount_amount: discount,
        plan_count: ownedPlanList.length,
      },
    });
    // Deliberately keyed on open state alone: the event marks the open, and
    // re-firing it whenever the pricing summary re-derives would inflate the step.
  }, [switchPlanModal]);

  useEffect(() => {
    if (!switchPlanModal) {
      // back to the URL-provided code (empty when there is none)
      setPromoCode(queryPromoCode);
    }
  }, [switchPlanModal, queryPromoCode]);

  return (
    <>
      <RotatingBorder
        className="mx-auto w-full max-w-7xl rounded-xl px-4 xl:px-0"
        cardClass="rounded-xl"
        animationDuration="6s"
        borderWidth={1}
      >
        <div className="relative z-10 flex flex-col items-center justify-between gap-5 rounded-[7px] border-[#7E68B0] bg-white py-3 sm:px-4 md:flex-row">
          {/* Loading overlay */}
          {loading && (
            <div className="absolute inset-0 z-20 flex cursor-not-allowed items-center justify-center rounded-xl bg-white/5 backdrop-blur-[0.5px]">
              <div className="animate-shimmer h-full w-full bg-linear-to-r from-transparent via-white/40 to-transparent bg-size-[200%_100%]" />
              <LoaderSpinner className="text-accent-blue absolute top-1 left-1 h-6 w-6" />
            </div>
          )}

          {/* Left content */}
          <div className="flex items-center gap-6">
            {/* Icon box */}
            <div className="rounded-lg bg-surface-muted p-3 text-type-accent">
              <SparkleIcon />
            </div>

            {/* Text content */}
            <div className="flex flex-col items-start gap-2">
              <h2 className="font-erode-variable text-2xl leading-[35.2px] font-normal tracking-[-0.72px] not-italic md:text-[32px]">
                {parse(formatTemplateString(conpyConfig.title, "33"))}
              </h2>
              <p className="text-type-dark-primary font-archivo text-sm leading-[19.6px] font-normal">
                {conpyConfig.description}
              </p>
            </div>
          </div>

          <Button
            onClick={handleSwitchToYearlyClick}
            disabled={loading}
            variant="accent-invert"
            size="sm"
            className="w-full sm:w-auto"
          >
            <span className="font-archivo text-sm leading-5 font-medium text-type-accent">
              {conpyConfig.button.text}
            </span>
          </Button>
        </div>
      </RotatingBorder>

      <SwitchIntervalDialog
        isOpen={switchPlanModal}
        isEstimating={isEstimating}
        onClose={closeSwitchPlanModal}
      >
        <SwitchIntervalCard
          content={switchIntervalContent}
          planName={switchIntervalCopy.planName}
          currency={planCurrency}
          targetInterval={TARGET_INTERVAL}
          currentPeriodEnd={currentPeriodEnd}
          subtotal={switchIntervalCopy.subtotal}
          renewNote={switchIntervalCopy.renewNote}
          isEstimating={isEstimating}
          isEstimateError={isEstimateError}
          estimateData={estimateData}
          isV1={isV1}
          onConfirm={closeSwitchPlanModal}
          promoCode={promoCode}
          footnote={
            isSaleActive ? switchIntervalContent.prorationFootnote : undefined
          }
          onApplyPromo={handleApplyPromo}
          onRemovePromo={handleRemovePromo}
        />
      </SwitchIntervalDialog>
    </>
  );
};

export default YearlyPlanSaleBanner;
